import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import type { ColumnInstance, TableInstance } from 'react-table';

import { TraffickingTableName } from '../../../../constants';
import type { Nullable } from '../../../../models';
import { formatMonetaryAmount, formatPercentage } from '../../../../utils/formatting';
import type { CustomColumnConfig } from '../../TraffickingPage/columns/utils';
import { ABSENT_EXPORT, isAbsent } from '../../TraffickingPage/columns/utils';
import type { AdModel, CampaignModel, EntityModel, LineItemModel } from '../../TraffickingPage/modelConverters';
import { NOT_SELECTED_ENTITY_MARK, SELECTED_ENTITY_MARK } from '../constants';
import type { EntityModelForExport, HeaderColumn } from '../types';

type ColumnInstanceWithExport<T extends object = {}> = ColumnInstance<T> & CustomColumnConfig<T>;

const getHeaderColumns = (columns: ColumnInstance<EntityModel>[]): HeaderColumn[] => {
  return columns.reduce(
    (acc, column) => {
      const columnWithExport = column as ColumnInstanceWithExport<EntityModel>;

      // Skip non-visible, non-string headers, and columns disabled for export
      if (
        !column.isVisible ||
        (typeof column.Header !== 'string' && columnWithExport.headerString === undefined) ||
        columnWithExport.disableForExport
      ) {
        return acc;
      }

      if (columnWithExport.exportColumns) {
        for (const exp of columnWithExport.exportColumns) {
          acc.push({ name: exp.header, id: `${column.id}__${exp.header}` });
        }
        return acc;
      }

      const headerName = typeof column.Header !== 'string' ? columnWithExport.headerString : column.Header;

      const headerCol: HeaderColumn = {
        name: headerName ?? '',
        id: column.id,
      };

      return [...acc, headerCol];
    },
    [
      {
        name: 'Selection',
        id: 'selection',
      },
    ]
  );
};

const isAdModels = (models: EntityModel[], tableName: TraffickingTableName): models is AdModel[] => {
  return tableName === TraffickingTableName.ads;
};

const isLineItemModels = (models: EntityModel[], tableName: TraffickingTableName): models is LineItemModel[] => {
  return tableName === TraffickingTableName.lineItems;
};

const isCampaignModels = (models: EntityModel[], tableName: TraffickingTableName): models is CampaignModel[] => {
  return tableName === TraffickingTableName.campaigns;
};

type GetModelDataToExportProps = {
  tableName: TraffickingTableName;
  tableInstance: TableInstance<EntityModel>;
  entities: EntityModelForExport[];
  selectedItems: string[];
};

export const getTableDataToExport = ({
  tableName,
  tableInstance,
  selectedItems,
}: Omit<GetModelDataToExportProps, 'entities'>): string[][] => {
  const entities: EntityModel[] = tableInstance.rows.map((row) => row.original);
  let entitiesForExport: EntityModelForExport[] = [];

  if (isCampaignModels(entities, tableName)) {
    entitiesForExport = entities;
  } else if (isLineItemModels(entities, tableName) || isAdModels(entities, tableName)) {
    entitiesForExport = entities;
  }

  return getModelDataToExport({
    tableName,
    tableInstance,
    entities: entitiesForExport,
    selectedItems,
  });
};

export const getHeaderRowToExport = (tableInstance: TableInstance<EntityModel>): string[] => {
  const headerColumns = getHeaderColumns(tableInstance.allColumns);

  return headerColumns.map((headerColumn) => headerColumn.name);
};

export const getDataRowsToExport = ({
  tableInstance,
  entities,
  selectedItems,
}: GetModelDataToExportProps): string[][] => {
  const headerColumns = getHeaderColumns(tableInstance.allColumns);

  // Build a map of column id to column config for fast lookup
  const columnMap = new Map<string, ColumnInstanceWithExport<EntityModel>>();
  for (const column of tableInstance.allColumns) {
    columnMap.set(column.id, column as ColumnInstanceWithExport<EntityModel>);
  }

  return getModelRowsValues({
    entities,
    headerColumns,
    columnMap,
    selectedColumnIds: selectedItems,
  });
};

export const getModelDataToExport = ({
  tableName,
  tableInstance,
  entities,
  selectedItems,
}: GetModelDataToExportProps): string[][] => {
  const columnNames = getHeaderRowToExport(tableInstance);
  let rowsValues: string[][] = getDataRowsToExport({
    tableName,
    tableInstance,
    entities,
    selectedItems,
  });

  return [columnNames, ...rowsValues];
};

type GetModelRowsValuesProps = {
  entities: EntityModelForExport[];
  headerColumns: HeaderColumn[];
  columnMap: Map<string, ColumnInstanceWithExport<EntityModel>>;
  selectedColumnIds: string[];
};

/**
 * Get the export value for a column using exportAccessor, accessor, or empty string fallback.
 */
const getColumnExportValue = (
  column: ColumnInstanceWithExport<EntityModel> | undefined,
  dataItem: EntityModelForExport
): string => {
  if (!column) return '';

  // Priority 1: Use exportAccessor if defined
  if (column.exportAccessor) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return column.exportAccessor(dataItem as any) ?? '';
  }

  // Priority 2: Fall back to accessor if it's a function
  if (typeof column.accessor === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value = column.accessor(dataItem as any, 0, { subRows: [], depth: 0, data: [] });
    return value?.toString() ?? '';
  }

  // Priority 3: Fall back to accessor if it's a string (property path)
  if (typeof column.accessor === 'string') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value = (dataItem as any)[column.accessor];
    return value?.toString() ?? '';
  }

  return '';
};

const getModelRowsValues = ({
  entities,
  headerColumns,
  columnMap,
  selectedColumnIds,
}: GetModelRowsValuesProps): string[][] => {
  const resultedData: string[][] = [];

  // Build a flat accessor map keyed by header column ID.
  // For exportColumns sub-entries, use the composite key and the sub-accessor directly.
  const accessorMap = new Map<string, (item: EntityModelForExport) => string>();
  for (const [id, column] of columnMap.entries()) {
    if (column.exportColumns) {
      for (const exp of column.exportColumns) {
        const compositeId = `${id}__${exp.header}`;
        accessorMap.set(compositeId, exp.accessor as (item: EntityModelForExport) => string);
      }
    }
    // Store a value getter for the base column ID
    accessorMap.set(id, (item) => getColumnExportValue(column, item));
  }

  for (const dataItem of entities) {
    const csvCells: string[] = [];

    // First cell is selection status
    csvCells.push(selectedColumnIds.includes(dataItem.id) ? SELECTED_ENTITY_MARK : NOT_SELECTED_ENTITY_MARK);

    // Process remaining columns (starting from index 1 to skip selection)
    for (let i = 1; i < headerColumns.length; i++) {
      const columnId = headerColumns[i].id;
      const accessor = accessorMap.get(columnId);
      csvCells.push(accessor ? accessor(dataItem) : '');
    }

    resultedData.push(csvCells);
  }

  return resultedData;
};

export const getPercentageValueToShow = (value?: Nullable<number>): string => {
  return isAbsent(value) ? ABSENT_EXPORT : formatPercentage(value);
};

export const getCurrencyValueToShow = (value?: Nullable<number>, currency?: string): string => {
  return isAbsent(value) ? ABSENT_EXPORT : formatMonetaryAmount(value, currency);
};

const convertCurrentDateToExportFileNameDate = (date: Date): string => {
  return format(date, "MMddyy'_'HHmma");
};

export const convertExportFileNameToCorrectFormat = (tableName: TraffickingTableName, date: Date): string => {
  return `${upperFirst(tableName).replace(/ /g, '')}_${convertCurrentDateToExportFileNameDate(date)}`;
};

export const getReversedEntities = (dataToExport: string[][]): string[][] => {
  let lastSelectedItemsIndex = -1;
  for (let exportRowIndex = 0; exportRowIndex < dataToExport.length; exportRowIndex++) {
    const columnSelected = dataToExport[exportRowIndex][0] && dataToExport[exportRowIndex][0] === SELECTED_ENTITY_MARK;
    if (columnSelected) {
      lastSelectedItemsIndex = exportRowIndex;
    } else {
      break;
    }
  }

  const selectedItems: string[][] =
    lastSelectedItemsIndex > -1 ? dataToExport.slice(0, lastSelectedItemsIndex + 1) : [];
  const notSelectedItems: string[][] = dataToExport.slice(lastSelectedItemsIndex + 1);

  return [...selectedItems.reverse(), ...notSelectedItems.reverse()];
};
